import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { CallSummaryPage } from '@karehero/llama/pwa/pages/CallSummaryPage/CallSummaryPage';
import type { PortableTextContent } from '@karehero/llama/components/organisms/RichTextEditor/portableText';

import { CallNoteContent } from '@karehero/models';
import { formatDayMonthTime } from '@karehero/helpers/datetime';
import { useGoBack } from 'hooks/useGoBack';

import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { useGetOrganiseDocumentQuery } from 'store/api/organiseDocument';

const ADVISOR_NAME = 'Your advisor';

export const CallSummary = () => {
  const goBack = useGoBack();
  const { documentID } = useParams<{ documentID: string }>();
  const careCircle = useSelector(getCurrentCareCircle);
  const careCircleId = careCircle?.id ?? '';

  const [callContent, setCallContent] = useState<CallNoteContent | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { data: document, isLoading: isDocumentLoading } =
    useGetOrganiseDocumentQuery(
      { careCircleId, documentId: documentID ?? '' },
      { skip: !careCircleId || !documentID },
    );

  useEffect(() => {
    const fetchContent = async () => {
      if (!document) return;

      if (!document.downloadUrl) {
        setHasError(true);
        Sentry.captureMessage('Call note document missing downloadUrl', {
          extra: { documentId: document.id },
        });
        return;
      }

      setIsLoadingContent(true);
      setHasError(false);

      try {
        const response = await fetch(document.downloadUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch call content');
        }
        const content: CallNoteContent = await response.json();
        setCallContent(content);
      } catch (error) {
        Sentry.captureException(error);
        setHasError(true);
      } finally {
        setIsLoadingContent(false);
      }
    };

    fetchContent();
  }, [document]);

  const isLoading = isDocumentLoading || isLoadingContent;

  const createdAtSource = callContent?.createdAt ?? document?.createdAt;
  const formattedWhen = createdAtSource
    ? formatDayMonthTime(createdAtSource)
    : undefined;

  return (
    <CallSummaryPage
      title={callContent?.title ?? document?.title ?? 'Call'}
      when={formattedWhen}
      withName={ADVISOR_NAME}
      content={callContent?.content as PortableTextContent | undefined}
      isLoading={isLoading}
      hasError={hasError}
      onBack={goBack}
    />
  );
};

export default CallSummary;
